import classNames from 'classnames';
import { useAppSelector } from 'hooks/useAppSelector';
import { useUserNotification } from 'hooks/useUserNotification';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import type { Observable } from 'rxjs';
import { UserNotification } from 'state/notifications/notification.model';
import { getIsUserInternal } from 'state/selectors';
import styled from 'styled-components';
import { BackendApiMessage, HttpError, isBackendErrorMessage } from 'utils/http/httpClient.model';

import { BlotterErrorIcon } from '../BlotterErrorIcon';
import { BlotterSpinner } from '../BlotterSpinner';

const SameSizeWrapper = styled.div`
  width: 3rem;
  height: 3rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

interface ExportCsvProps {
  fetch: () => Observable<void>;
  withNotification?: boolean;
  disabled?: boolean;

  className?: string;
}

function getErrorDetails(
  isInternalUser: boolean,
  error: BackendApiMessage | HttpError<BackendApiMessage>,
): UserNotification['details'] {
  if (isBackendErrorMessage(error)) {
    if (isInternalUser) {
      return [
        {
          id: 'blotter.export.csv.failure.details' as const,
          values: {
            message: error.message,
            origin: error.origin,
          },
        },
      ];
    }
  } else {
    const hasTooManyRows = `${error}`.search('Too many rows') > 0;
    if (hasTooManyRows) {
      return [
        {
          id: 'blotter.export.csv.failure.tooLarge' as const,
        },
      ];
    }
  }
}

function useExportCsv(fetch: () => Observable<void>): [boolean, boolean, () => void] {
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [hasExportError, setExportError] = useState<boolean>(false);
  const isInternalUser = useAppSelector(getIsUserInternal);

  const notification = useUserNotification();
  function exportCsv() {
    setIsExporting(true);
    setExportError(false);
    fetch().subscribe({
      next() {
        setIsExporting(false);
        notification.success({ title: { id: 'toast.export' }, content: [{ id: 'blotter.export.csv.success' }] });
        setExportError(false);
      },
      error(error: BackendApiMessage | HttpError<BackendApiMessage>) {
        setIsExporting(false);
        const details = getErrorDetails(isInternalUser, error);

        notification.error({
          title: { id: 'toast.export' },
          content: [{ id: 'blotter.export.csv.failure' }],
          details,
        });
        setExportError(true);
      },
    });
  }
  return [isExporting, hasExportError, exportCsv];
}

export const ExportCsv: FC<ExportCsvProps> = ({ fetch, disabled, className }) => {
  const { formatMessage } = useIntl();
  const [isExporting, hasExportError, exportCsv] = useExportCsv(fetch);

  return (
    <>
      {isExporting ? (
        <SameSizeWrapper>
          <BlotterSpinner />
        </SameSizeWrapper>
      ) : (
        <button
          className={classNames('btn btn-flat-secondary btn-xl btn-icon border-start border-opacity-40', className)}
          title={formatMessage({ id: 'blotter.filter.label.exportCSV' })}
          data-e2e="export-csv-btn"
          onClick={exportCsv}
          disabled={disabled}
        >
          <i className="icon icon-sm">save_alt</i>
        </button>
      )}
      {hasExportError && <BlotterErrorIcon message={formatMessage({ id: 'downloadCsv.Error' })} />}
    </>
  );
};
